import "./SotGa4LabelingTable.scss";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import * as R from "ramda";
import {
  CellRenderer,
  CheckBox,
  CornerLocation,
  FrozenColumnsTable,
  Header,
  SelfFocusingSelect,
} from "../Components";
import { StateSetter } from "../utils/types";
import { useMap } from "../utils/hooks/useData";
import { SotGa4EditsMap, SotGa4Row, SotGa4RowEdit, SotGa4EditsMapAction } from "./SotGa4Labeling";
import { CustomSegmentsData } from "./SotGa4MappingTool";

interface SotGa4LabelingTableProps {
  data: any[];
  customSegments: CustomSegmentsData[] | undefined;
  editsMap: SotGa4EditsMap;
  selectedRows: Record<string, SotGa4Row>;
  dispatchEditsMap: React.Dispatch<SotGa4EditsMapAction>;
  setSelectedRows: StateSetter<Record<string, SotGa4Row>>;
}

const SotGa4LabelingTable: React.FC<SotGa4LabelingTableProps> = ({
  data,
  customSegments,
  editsMap,
  selectedRows,
  dispatchEditsMap,
  setSelectedRows,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [focusedCell, setFocusedCell] = useState("");
  const [resizeActive, setResizeActive] = useState(false);
  const [resizedLeftWidth, setResizedLeftWidth] = useState<number | undefined>();
  const [innerWidth, setInnerHeight] = useState<number>(window.innerWidth);

  useEffect(() => {
    window.addEventListener("resize", () => setInnerHeight(window.innerWidth));

    return () => {
      window.removeEventListener("resize", () => setInnerHeight(window.innerWidth));
    };
  }, [innerWidth]);

  const selectedRowsSet = useMemo(() => new Set(Object.keys(selectedRows)), [selectedRows]);

  const selectRow = useCallback(
    (row: SotGa4Row) => {
      const { campaign_key } = row;
      if (!campaign_key) {
        return;
      }

      if (R.has(campaign_key, selectedRows)) {
        setSelectedRows(current => R.omit([campaign_key], current));
      } else {
        setSelectedRows(current => ({ ...current, [campaign_key]: row }));
      }
    },
    [selectedRows, setSelectedRows]
  );

  const dataWithEdits = useMemo(() => {
    return data.map(row => {
      let rowToUse = { ...row };
      if (row.campaign_key && editsMap[row.campaign_key]) {
        rowToUse = editsMap[row.campaign_key];
      }
      return rowToUse;
    });
  }, [editsMap, data]);

  const dataMap = useMemo(() => {
    let output = {};
    data.forEach(row => {
      if (row.campaign_key) {
        output[row.campaign_key] = { ...row };
      }
    });
    return output;
  }, [data]);

  const rightTableHeaders: Header[] = useMemo(() => {
    const customSegmentHeaders = (customSegments || []).map(segment => ({
      label: segment.segmentName,
      name: segment.segmentName,
      width: 250,
      renderer: (row: SotGa4Row) => {
        if (row && row.campaign_key) {
          const rowToUse = editsMap[row.campaign_key] || row;
          const { campaign_key } = rowToUse;

          if (!campaign_key) {
            return <div></div>;
          }

          const val =
            (R.has(segment.segmentName, rowToUse.edits)
              ? (rowToUse.edits as SotGa4RowEdit)[segment.segmentName]?.label
              : rowToUse[segment.segmentName.toLowerCase()]) || "";

          return (
            <>
              {focusedCell === `${segment.segmentName}_${campaign_key}` ? (
                <SelfFocusingSelect
                  menuPortalTarget={document.body}
                  className="selectContainer"
                  value={
                    val
                      ? {
                          label: val,
                          value: val,
                        }
                      : null
                  }
                  placeholder={`Select ${segment.segmentName}`}
                  onChange={selection => {
                    const action = selection
                      ? (input: { rows: SotGa4Row[]; edits: SotGa4RowEdit }) => {
                          dispatchEditsMap({
                            type: "UPDATE_SEGMENT_EDIT",
                            updates: [{ rows: input.rows, edits: input.edits }],
                          });
                        }
                      : (input: { segmentName: string; keys: string[] }) =>
                          dispatchEditsMap({
                            type: "DELETE_SEGMENT_EDIT",
                            segmentName: input.segmentName,
                            campaign_keys: input.keys,
                          });

                    if (selectedRowsSet.has(campaign_key)) {
                      action({
                        rows: Object.values(selectedRows),
                        edits: { [segment.segmentName]: selection },
                        keys: Array.from(selectedRowsSet),
                        segmentName: segment.segmentName,
                      });
                    } else {
                      action({
                        rows: [rowToUse],
                        edits: { [segment.segmentName]: selection },
                        keys: [campaign_key],
                        segmentName: segment.segmentName,
                      });
                    }
                  }}
                  onBlur={() => setFocusedCell("")}
                  options={segment.values.map(value => {
                    return { label: value.valueName, value: value.valueId };
                  })}
                  isClearable
                  defaultMenuIsOpen
                />
              ) : (
                <div
                  className="editableCell"
                  onClick={() => setFocusedCell(`${segment.segmentName}_${campaign_key}`)}
                >
                  <span className="cellText">{val || "-"}</span>
                </div>
              )}
            </>
          );
        } else {
          return <div></div>;
        }
      },
    }));

    return customSegmentHeaders;
  }, [customSegments, dispatchEditsMap, editsMap, focusedCell, selectedRows, selectedRowsSet]);

  const rightHeaderNames = useMemo(() => {
    return (rightTableHeaders || []).map(header => header.name || "");
  }, [rightTableHeaders]);

  const customLeftRenderer: CellRenderer<any> = useMemo(
    () => ({ data, style = {}, classes = [] }) => {
      const {
        campaign_key,
        raw_source,
        raw_medium,
        campaign_name,
        campaign_id,
        ad_group_name,
        ad_group_id,
        ad_name,
        ad_id,
      } = data;

      return (
        <div style={style} className={[...classes, "leftCell"].join(" ")}>
          <div className="checkBoxContainer">
            <CheckBox
              className="checkbox"
              checked={campaign_key ? R.has(campaign_key, selectedRows) : false}
              onCheck={() => campaign_key && selectRow(dataMap[campaign_key])}
            />
          </div>
          <div className="leftCellDiv raw_source">
            <span className="cellText">{raw_source || "-"}</span>
          </div>
          <div className="leftCellDiv raw_medium">
            <span className="cellText">{raw_medium || "-"}</span>
          </div>
          <div className="leftCellDiv campaign_name">
            <span className="cellText">{campaign_name || "-"}</span>
          </div>
          <div className="leftCellDiv campaign_id">
            <span className="cellText">{campaign_id || "-"}</span>
          </div>
          <div className="leftCellDiv ad_group_name">
            <span className="cellText">{ad_group_name || "-"}</span>
          </div>
          <div className="leftCellDiv ad_group_id">
            <span className="cellText">{ad_group_id || "-"}</span>
          </div>
          <div className="leftCellDiv ad_name">
            <span className="cellText">{ad_name || "-"}</span>
          </div>
          <div className="leftCellDiv ad_id">
            <span className="cellText">{ad_id || "-"}</span>
          </div>
        </div>
      );
    },
    [dataMap, selectRow, selectedRows]
  );

  const [rightData, setRightData] = useState<any[][]>([]);

  useEffect(() => {
    if (dataWithEdits.length > 0 && rightTableHeaders.length > 0) {
      const originalIndexMap = {};
      for (let i = 0; i < dataWithEdits.length; i++) {
        if (dataWithEdits[i].campaign_key) {
          originalIndexMap[dataWithEdits[i].campaign_key] = i;
        }
      }

      let data: any[][] = [];
      for (let i = 0; i < dataWithEdits.length; ++i) {
        let obj = dataWithEdits[i];
        let row: any[] = [];
        for (let { name } of rightTableHeaders) {
          let content = obj[R.defaultTo("undefined", name)];
          row.push({
            originalRowIndex: obj.campaign_key ? originalIndexMap[obj.campaign_key] : undefined,
            content: R.isNil(content) ? "" : content,
          });
        }
        data.push(row);
      }
      setRightData(data);
    }
  }, [dataWithEdits, rightTableHeaders]);

  const sortRightData = useCallback(
    (columnName: string, direction: string) => {
      let tempData = [...dataWithEdits];

      if (direction === "up") {
        tempData.sort((a, b) => {
          const valA = a[columnName] || "";
          const valB = b[columnName] || "";
          return valA.localeCompare(valB);
        });
      }
      if (direction === "down") {
        tempData.sort((a, b) => {
          const valA = a[columnName] || "";
          const valB = b[columnName] || "";
          return valB.localeCompare(valA);
        });
      }

      const originalIndexMap = {};
      for (let i = 0; i < dataWithEdits.length; i++) {
        if (dataWithEdits[i].campaign_key) {
          originalIndexMap[dataWithEdits[i].campaign_key] = i;
        }
      }

      let data: any[][] = [];
      for (let i = 0; i < tempData.length; ++i) {
        let obj = tempData[i];
        let row: any[] = [];
        for (let { name } of rightTableHeaders) {
          let content = obj[R.defaultTo("undefined", name)];
          row.push({
            originalRowIndex: obj.campaign_key ? originalIndexMap[obj.campaign_key] : undefined,
            content: R.isNil(content) ? "" : content,
          });
        }
        data.push(row);
      }
      setRightData(data);
    },
    [dataWithEdits, rightTableHeaders]
  );

  const [sortMap, setSortMapValue, setSortMap] = useMap<string, string>({});

  const updateSortingMap = useMemo(() => {
    return { off: "up", up: "down", down: "off" };
  }, []);

  const onSort = useCallback(
    (propertyName, event) => {
      if (event.target instanceof HTMLElement) {
        const { classList } = event.target;
        if (classList.contains("bf-label") || classList.contains("toggle-item")) {
          return;
        }
      }
      setSortMap({});
      setSortMapValue(propertyName, updateSortingMap[sortMap[propertyName] || "off"]);
      sortRightData(propertyName, updateSortingMap[sortMap[propertyName] || "off"]);
    },
    [setSortMap, setSortMapValue, sortMap, sortRightData, updateSortingMap]
  );

  const customCornerRenderer = useMemo(
    () => (corner: CornerLocation) => {
      if (corner === "nw") {
        return (
          <div
            className="stickyTableCell"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <div className="checkBoxContainer">
              <CheckBox
                checked={selectAll}
                onCheck={() => {
                  if (selectAll) {
                    setSelectedRows({});
                  } else {
                    let selectAllMap = {};
                    if (data) {
                      for (let row of data) {
                        if (row.campaign_key) {
                          selectAllMap[row.campaign_key] = row;
                        }
                      }
                    }

                    setSelectedRows(current => ({ ...current, ...selectAllMap }));
                  }

                  setSelectAll(!selectAll);
                }}
              />
            </div>
            <div
              className={`includeSortHeaderCaret raw_source ${sortMap.raw_source}`}
              onClick={e => {
                onSort("raw_source", e);
              }}
            >
              Raw Source
            </div>
            <div
              className={`includeSortHeaderCaret raw_medium ${sortMap.raw_medium}`}
              onClick={e => {
                onSort("raw_medium", e);
              }}
            >
              Raw Medium
            </div>
            <div
              className={`includeSortHeaderCaret campaign_name ${sortMap.campaign_name}`}
              onClick={e => {
                onSort("campaign_name", e);
              }}
            >
              Campaign Name
            </div>
            <div
              className={`includeSortHeaderCaret campaign_id ${sortMap.campaign_id}`}
              onClick={e => {
                onSort("campaign_id", e);
              }}
            >
              Campaign ID
            </div>
            <div
              className={`includeSortHeaderCaret ad_group_name ${sortMap.ad_group_name}`}
              onClick={e => {
                onSort("ad_group_name", e);
              }}
            >
              Ad Group Name
            </div>
            <div
              className={`includeSortHeaderCaret ad_group_id ${sortMap.ad_group_id}`}
              onClick={e => {
                onSort("ad_group_id", e);
              }}
            >
              Ad Group ID
            </div>
            <div
              className={`includeSortHeaderCaret ad_name ${sortMap.ad_name}`}
              onClick={e => {
                onSort("ad_name", e);
              }}
            >
              Ad Name
            </div>
            <div
              className={`includeSortHeaderCaret ad_id ${sortMap.ad_id}`}
              onClick={e => {
                onSort("ad_id", e);
              }}
            >
              Ad ID
            </div>
          </div>
        );
      } else {
        return <div />;
      }
    },
    [
      selectAll,
      sortMap.raw_source,
      sortMap.raw_medium,
      sortMap.campaign_name,
      sortMap.campaign_id,
      sortMap.ad_group_name,
      sortMap.ad_group_id,
      sortMap.ad_name,
      sortMap.ad_id,
      setSelectedRows,
      data,
      onSort,
    ]
  );

  const leftDataFields = useMemo(() => {
    return [
      "campaign_key",
      "raw_source",
      "raw_medium",
      "campaign_name",
      "campaign_id",
      "ad_group_name",
      "ad_group_id",
      "ad_name",
      "ad_id",
    ];
  }, []);

  const noRowsRenderer = useCallback(() => {
    return data.length === 0 ? <div className="noRows">No rows to display.</div> : <div />;
  }, [data.length]);

  const startingWidth = useMemo(() => {
    return (3 / 4) * innerWidth;
  }, [innerWidth]);

  const leftWidth = useMemo(() => {
    return resizedLeftWidth ?? startingWidth;
  }, [resizedLeftWidth, startingWidth]);

  const leftSpacing = 24;
  const resizeFrame: React.MouseEventHandler<HTMLDivElement> = e => {
    if (resizeActive) {
      const leftNavWidth = 60;
      const halfAreaWidth = 10;
      const adjustedPosition = e.clientX - leftNavWidth - leftSpacing - halfAreaWidth;
      setResizedLeftWidth(adjustedPosition);
    }
  };

  return (
    <div
      className="labelingTable"
      onMouseUp={() => setResizeActive(false)}
      onMouseMove={e => resizeFrame(e)}
    >
      <div className="resizeContainer">
        <div
          className="resizeArea"
          style={{ left: `${leftWidth + 24}px` }}
          onMouseDown={() => setResizeActive(true)}
        />
      </div>
      <FrozenColumnsTable
        leftWidth={leftWidth}
        leftRenderer={customLeftRenderer}
        cornerRenderer={customCornerRenderer}
        rightTableHeaders={rightTableHeaders}
        rightTableHeaderNames={rightHeaderNames}
        originalRightData={data}
        leftDataFields={leftDataFields}
        rightData={rightData}
        setRightData={setRightData}
        noRowsRenderer={noRowsRenderer}
      />
    </div>
  );
};

export default SotGa4LabelingTable;
